import { useState } from 'preact/hooks';
import { fetchVTData, executeVTImport } from '../../lib/api';

const STEPS = {
  CONNECT: 1,
  DEPARTMENTS: 2,
  USERS: 3,
  LEAVE_TYPES: 4,
  REVIEW: 5,
  IMPORTING: 6,
  COMPLETE: 7
};

const stepLabels = ['Connect', 'Departments', 'Users', 'Leave Types', 'Review'];

export default function VTImportPopup({ onClose, onComplete }) {
  const [step, setStep] = useState(STEPS.CONNECT);
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [vtData, setVtData] = useState(null);
  const [unplugData, setUnplugData] = useState(null);

  const [deptMappings, setDeptMappings] = useState([]);
  const [userMappings, setUserMappings] = useState([]);
  const [leaveTypeMappings, setLeaveTypeMappings] = useState([]);
  const [sampleLeaves, setSampleLeaves] = useState([]);

  const [includeDepts, setIncludeDepts] = useState(true);
  const [includeUsers, setIncludeUsers] = useState(true);
  const [includeLeaveTypes, setIncludeLeaveTypes] = useState(true);
  const [includeLeaveRequests, setIncludeLeaveRequests] = useState(false);
  const [leaveHistoryAvailable, setLeaveHistoryAvailable] = useState(false);

  const [result, setResult] = useState(null);

  const handleFetchData = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your API key');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const data = await fetchVTData(apiKey.trim());
      setVtData(data.vacationtracker);
      setUnplugData(data.unplug);

      const deptMaps = (data.vacationtracker.departments || []).map(vt => {
        const match = data.unplug.departments.find(u => u.name.toLowerCase() === vt.name.toLowerCase());
        return { vtId: vt.id, vtName: vt.name, action: match ? 'map' : 'create', unplugId: match?.id || '' };
      });
      setDeptMappings(deptMaps);

      const userMaps = (data.vacationtracker.users || []).map(vt => {
        const match = data.unplug.users.find(u => u.email?.toLowerCase() === vt.email?.toLowerCase());
        return { vtId: vt.id, vtName: vt.name, vtEmail: vt.email, action: match ? 'map' : 'create', unplugId: match?.id || '' };
      });
      setUserMappings(userMaps);

      const ltMaps = (data.vacationtracker.leaveTypes || []).map(vt => {
        const match = data.unplug.leaveTypes.find(u => u.name.toLowerCase() === vt.name.toLowerCase());
        // When mapping, use the target leave type's unit; when creating, default to 'days'
        const targetUnit = match?.unit || 'days';
        return {
          vtId: vt.id,
          vtName: vt.name,
          vtColor: vt.color,
          action: match ? 'map' : 'create',
          unplugId: match?.id || '',
          importUnit: targetUnit  // User can override this
        };
      });
      setLeaveTypeMappings(ltMaps);

      // Store sample leaves for user reference
      setSampleLeaves(data.vacationtracker.sampleLeaves || []);

      const hasLeaveData = (data.vacationtracker.leaveRequestCount || 0) > 0;
      setLeaveHistoryAvailable(hasLeaveData);
      setIncludeLeaveRequests(hasLeaveData);

      setStep(STEPS.DEPARTMENTS);
    } catch (err) {
      setError(err.message || 'Failed to connect');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setStep(STEPS.IMPORTING);
    setError('');

    try {
      const mappings = {
        departments: { enabled: includeDepts, items: deptMappings },
        users: { enabled: includeUsers, items: userMappings },
        leaveTypes: { enabled: includeLeaveTypes, items: leaveTypeMappings },
        leaveRequests: { enabled: includeLeaveRequests }
      };

      const data = await executeVTImport(apiKey.trim(), mappings);
      setResult({ stats: data.stats, debug: data.debug });
      setStep(STEPS.COMPLETE);
    } catch (err) {
      setError(err.message || 'Import failed');
      setStep(STEPS.REVIEW);
    }
  };

  const handleClose = () => {
    if (step === STEPS.COMPLETE && onComplete) {
      onComplete();
    }
    onClose();
  };

  const updateMapping = (type, index, field, value) => {
    const setters = { dept: setDeptMappings, user: setUserMappings, lt: setLeaveTypeMappings };
    setters[type](prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // When mapping to a different leave type, update importUnit to match target's unit
      if (type === 'lt' && field === 'unplugId' && value) {
        const targetLeaveType = unplugData?.leaveTypes?.find(lt => lt.id === value);
        if (targetLeaveType) {
          updated[index].importUnit = targetLeaveType.unit || 'days';
        }
      }

      return updated;
    });
  };

  const currentStepIndex = Math.min(step, 5);

  // Styles object for cleaner JSX
  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px'
    },
    backdrop: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%)',
      backdropFilter: 'blur(8px)'
    },
    container: {
      position: 'relative',
      width: '100%',
      maxWidth: '680px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      background: 'rgba(255,255,255,0.15)',
      borderRadius: '16px 16px 0 0',
      padding: '20px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    headerIcon: {
      width: '44px',
      height: '44px',
      background: 'rgba(255,255,255,0.2)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    card: {
      background: 'white',
      borderRadius: '0 0 16px 16px',
      boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
      overflow: 'hidden',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(90vh - 140px)'
    },
    progressBar: {
      background: 'rgba(255,255,255,0.1)',
      padding: '16px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      flexWrap: 'wrap'
    },
    stepPill: (active, completed) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 14px',
      borderRadius: '20px',
      background: active ? 'white' : completed ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
      color: active ? '#7c3aed' : 'white',
      fontSize: '13px',
      fontWeight: '500',
      transition: 'all 0.2s'
    }),
    button: (primary, disabled) => ({
      padding: primary ? '12px 28px' : '10px 20px',
      background: disabled ? '#d1d5db' : primary ? 'linear-gradient(135deg, #7c3aed, #6d28d9)' : '#f3f4f6',
      color: primary ? 'white' : '#374151',
      border: 'none',
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s'
    }),
    input: {
      width: '100%',
      padding: '14px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      fontSize: '15px',
      outline: 'none',
      boxSizing: 'border-box',
      transition: 'border-color 0.2s'
    },
    select: {
      padding: '8px 12px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '13px',
      background: 'white',
      cursor: 'pointer'
    },
    error: {
      background: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '10px',
      padding: '12px 16px',
      marginBottom: '20px',
      color: '#dc2626',
      fontSize: '14px'
    },
    scrollArea: {
      padding: '16px 24px',
      overflowY: 'auto',
      flex: 1
    }
  };

  return (
    <div style={styles.overlay}>
      {/* Gradient Backdrop */}
      <div style={styles.backdrop} onClick={step !== STEPS.IMPORTING ? handleClose : undefined} />

      {/* Popup Container */}
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={styles.headerIcon}>
              <svg width="22" height="22" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            </div>
            <div>
              <h1 style={{ margin: 0, color: 'white', fontSize: '18px', fontWeight: '600' }}>Import from VacationTracker.io</h1>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Migrate your leave management data</p>
            </div>
          </div>
          {step !== STEPS.IMPORTING && (
            <button
              onClick={handleClose}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                borderRadius: '8px',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white'
              }}
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Progress Steps */}
        {step <= STEPS.REVIEW && (
          <div style={styles.progressBar}>
            {stepLabels.map((label, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center' }}>
                <div style={styles.stepPill(idx + 1 === currentStepIndex, idx + 1 < currentStepIndex)}>
                  {idx + 1 < currentStepIndex ? (
                    <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <span style={{ fontSize: '12px', fontWeight: '600' }}>{idx + 1}</span>
                  )}
                  <span>{label}</span>
                </div>
                {idx < stepLabels.length - 1 && (
                  <div style={{ width: '16px', height: '2px', background: idx + 1 < currentStepIndex ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)', margin: '0 4px' }} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Main Content Card */}
        <div style={styles.card}>

          {/* Step 1: Connect */}
          {step === STEPS.CONNECT && (
            <div style={{ padding: '36px 32px' }}>
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <div style={{ width: '60px', height: '60px', background: 'linear-gradient(135deg, #a78bfa, #7c3aed)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                  <svg width="26" height="26" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <h2 style={{ margin: '0 0 6px', fontSize: '22px', fontWeight: '600', color: '#1f2937' }}>Connect Your Account</h2>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>Enter your VacationTracker.io API key to get started</p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>API Key</label>
                <input
                  type="password"
                  value={apiKey}
                  onInput={(e) => setApiKey(e.target.value)}
                  placeholder="Paste your API key here..."
                  style={styles.input}
                  onFocus={(e) => e.target.style.borderColor = '#a78bfa'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              <div style={{ background: '#f3f4f6', borderRadius: '12px', padding: '14px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ width: '32px', height: '32px', background: '#ddd6fe', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="16" height="16" fill="none" stroke="#7c3aed" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Where to find your API key</p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>VacationTracker.io → Settings → Integrations → API</p>
                  </div>
                </div>
              </div>

              {error && <div style={styles.error}>{error}</div>}

              <button onClick={handleFetchData} disabled={loading} style={styles.button(true, loading)}>
                {loading ? 'Connecting...' : 'Connect & Continue'}
              </button>
            </div>
          )}

          {/* Step 2: Departments */}
          {step === STEPS.DEPARTMENTS && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h2 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '600', color: '#1f2937' }}>Map Departments</h2>
                    <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>Match to existing or create new</p>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={includeDepts} onChange={(e) => setIncludeDepts(e.target.checked)} style={{ width: '18px', height: '18px', accentColor: '#7c3aed' }} />
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Include</span>
                  </label>
                </div>
              </div>
              <div style={styles.scrollArea}>
                {!includeDepts ? (
                  <div style={{ textAlign: 'center', padding: '36px', color: '#9ca3af' }}>Skipping departments</div>
                ) : deptMappings.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '36px', color: '#9ca3af' }}>No departments found</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {deptMappings.map((dept, idx) => (
                      <div key={dept.vtId} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f9fafb', borderRadius: '10px' }}>
                        <div style={{ width: '36px', height: '36px', background: '#ddd6fe', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="18" height="18" fill="none" stroke="#7c3aed" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>{dept.vtName}</p>
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: dept.action === 'create' ? '#dcfce7' : '#dbeafe', color: dept.action === 'create' ? '#166534' : '#1e40af' }}>
                            {dept.action === 'create' ? 'New' : dept.action === 'skip' ? 'Skip' : 'Link'}
                          </span>
                        </div>
                        <select value={dept.action} onChange={(e) => updateMapping('dept', idx, 'action', e.target.value)} style={styles.select}>
                          <option value="create">Create new</option>
                          <option value="map">Map to existing</option>
                          <option value="skip">Skip</option>
                        </select>
                        {dept.action === 'map' && (
                          <select value={dept.unplugId} onChange={(e) => updateMapping('dept', idx, 'unplugId', e.target.value)} style={{ ...styles.select, maxWidth: '140px' }}>
                            <option value="">Select...</option>
                            {unplugData.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
                <button onClick={() => setStep(STEPS.CONNECT)} style={styles.button(false, false)}>Back</button>
                <button onClick={() => setStep(STEPS.USERS)} style={styles.button(true, false)}>Continue</button>
              </div>
            </div>
          )}

          {/* Step 3: Users */}
          {step === STEPS.USERS && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h2 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '600', color: '#1f2937' }}>Map Users</h2>
                    <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>Match by email or create new</p>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={includeUsers} onChange={(e) => setIncludeUsers(e.target.checked)} style={{ width: '18px', height: '18px', accentColor: '#7c3aed' }} />
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Include</span>
                  </label>
                </div>
              </div>
              <div style={styles.scrollArea}>
                {!includeUsers ? (
                  <div style={{ textAlign: 'center', padding: '36px', color: '#9ca3af' }}>Skipping users</div>
                ) : userMappings.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '36px', color: '#9ca3af' }}>No users found</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {userMappings.map((user, idx) => (
                      <div key={user.vtId} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f9fafb', borderRadius: '10px' }}>
                        <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #a78bfa, #7c3aed)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px', fontWeight: '600', flexShrink: 0 }}>
                          {user.vtName?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.vtName}</p>
                          <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.vtEmail}</p>
                        </div>
                        <select value={user.action} onChange={(e) => updateMapping('user', idx, 'action', e.target.value)} style={styles.select}>
                          <option value="create">Create new</option>
                          <option value="map">Map to existing</option>
                          <option value="skip">Skip</option>
                        </select>
                        {user.action === 'map' && (
                          <select value={user.unplugId} onChange={(e) => updateMapping('user', idx, 'unplugId', e.target.value)} style={{ ...styles.select, maxWidth: '140px' }}>
                            <option value="">Select...</option>
                            {unplugData.users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
                <button onClick={() => setStep(STEPS.DEPARTMENTS)} style={styles.button(false, false)}>Back</button>
                <button onClick={() => setStep(STEPS.LEAVE_TYPES)} style={styles.button(true, false)}>Continue</button>
              </div>
            </div>
          )}

          {/* Step 4: Leave Types */}
          {step === STEPS.LEAVE_TYPES && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h2 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '600', color: '#1f2937' }}>Map Leave Types</h2>
                    <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>Match or create leave types and confirm units</p>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={includeLeaveTypes} onChange={(e) => setIncludeLeaveTypes(e.target.checked)} style={{ width: '18px', height: '18px', accentColor: '#7c3aed' }} />
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Include</span>
                  </label>
                </div>
              </div>
              <div style={styles.scrollArea}>
                {!includeLeaveTypes ? (
                  <div style={{ textAlign: 'center', padding: '36px', color: '#9ca3af' }}>Skipping leave types</div>
                ) : leaveTypeMappings.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '36px', color: '#9ca3af' }}>No leave types found</div>
                ) : (
                  <>
                    {/* Sample leaves info box */}
                    {sampleLeaves.length > 0 && includeLeaveRequests && (
                      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                          <svg width="18" height="18" fill="none" stroke="#3b82f6" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: '2px' }}>
                            <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '600', color: '#1e40af' }}>Sample Leave Requests from VacationTracker</p>
                            <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#3b82f6' }}>
                              Review these samples to confirm the unit (days vs hours) for each leave type:
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {sampleLeaves.slice(0, 3).map((sample, i) => {
                                const ltName = vtData?.leaveTypes?.find(lt => lt.id === sample.leaveTypeId)?.name || 'Unknown';
                                return (
                                  <div key={i} style={{ fontSize: '11px', color: '#1e40af', fontFamily: 'monospace', background: 'rgba(255,255,255,0.5)', padding: '4px 8px', borderRadius: '4px' }}>
                                    {ltName}: {sample.startDate} → {sample.endDate} = <strong>{sample.durationDays}</strong> {sample.durationDays === 1 ? 'unit' : 'units'}
                                  </div>
                                );
                              })}
                            </div>
                            <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#6b7280' }}>
                              If "1" means 1 day, select "Days". If "8" means 1 day, select "Hours".
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {leaveTypeMappings.map((lt, idx) => (
                        <div key={lt.vtId} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f9fafb', borderRadius: '10px', flexWrap: 'wrap' }}>
                          <div style={{ width: '36px', height: '36px', background: lt.vtColor || '#7c3aed', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <svg width="18" height="18" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div style={{ flex: 1, minWidth: '100px' }}>
                            <p style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>{lt.vtName}</p>
                          </div>
                          <select value={lt.action} onChange={(e) => updateMapping('lt', idx, 'action', e.target.value)} style={styles.select}>
                            <option value="create">Create new</option>
                            <option value="map">Map to existing</option>
                            <option value="skip">Skip</option>
                          </select>
                          {lt.action === 'map' && (
                            <select value={lt.unplugId} onChange={(e) => updateMapping('lt', idx, 'unplugId', e.target.value)} style={{ ...styles.select, maxWidth: '140px' }}>
                              <option value="">Select...</option>
                              {unplugData.leaveTypes.map(l => <option key={l.id} value={l.id}>{l.name} ({l.unit || 'days'})</option>)}
                            </select>
                          )}
                          {lt.action !== 'skip' && (
                            <select
                              value={lt.importUnit || 'days'}
                              onChange={(e) => updateMapping('lt', idx, 'importUnit', e.target.value)}
                              style={{ ...styles.select, background: lt.importUnit === 'hours' ? '#fef3c7' : '#dcfce7' }}
                              title="How should VT durations be interpreted?"
                            >
                              <option value="days">Days (1 = 1 day)</option>
                              <option value="hours">Hours (1 = 1 hour)</option>
                            </select>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
                <button onClick={() => setStep(STEPS.USERS)} style={styles.button(false, false)}>Back</button>
                <button onClick={() => setStep(STEPS.REVIEW)} style={styles.button(true, false)}>Review</button>
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {step === STEPS.REVIEW && (
            <div style={{ padding: '28px 32px' }}>
              <h2 style={{ margin: '0 0 20px', fontSize: '18px', fontWeight: '600', color: '#1f2937', textAlign: 'center' }}>Review Your Import</h2>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                {[
                  { label: 'Departments', enabled: includeDepts, items: deptMappings },
                  { label: 'Users', enabled: includeUsers, items: userMappings },
                  { label: 'Leave Types', enabled: includeLeaveTypes, items: leaveTypeMappings },
                  { label: 'Leave Requests', enabled: includeLeaveRequests && leaveHistoryAvailable, count: vtData?.leaveRequestCount || 0 }
                ].map((item, idx) => (
                  <div key={idx} style={{ background: item.enabled ? '#f5f3ff' : '#f9fafb', border: `2px solid ${item.enabled ? '#c4b5fd' : '#e5e7eb'}`, borderRadius: '12px', padding: '14px' }}>
                    <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>{item.label}</p>
                    {item.enabled ? (
                      item.items ? (
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          <span style={{ color: '#059669' }}>{item.items.filter(i => i.action === 'create').length} new</span>
                          {' • '}
                          <span style={{ color: '#2563eb' }}>{item.items.filter(i => i.action === 'map').length} linked</span>
                        </div>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#059669' }}>{item.count} to import</span>
                      )
                    ) : (
                      <span style={{ fontSize: '12px', color: '#9ca3af' }}>Skipped</span>
                    )}
                  </div>
                ))}
              </div>

              {leaveHistoryAvailable && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px', marginBottom: '20px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={includeLeaveRequests} onChange={(e) => setIncludeLeaveRequests(e.target.checked)} style={{ width: '18px', height: '18px', accentColor: '#7c3aed' }} />
                  <span style={{ fontSize: '13px', color: '#92400e' }}>Import {vtData?.leaveRequestCount || 0} approved leave requests</span>
                </label>
              )}

              {error && <div style={styles.error}>{error}</div>}

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button onClick={() => setStep(STEPS.LEAVE_TYPES)} style={styles.button(false, false)}>Back</button>
                <button onClick={handleImport} style={{ ...styles.button(true, false), background: 'linear-gradient(135deg, #10b981, #059669)' }}>Start Import</button>
              </div>
            </div>
          )}

          {/* Step 6: Importing */}
          {step === STEPS.IMPORTING && (
            <div style={{ padding: '60px 40px', textAlign: 'center' }}>
              <div style={{ width: '60px', height: '60px', margin: '0 auto 24px', position: 'relative' }}>
                <div style={{ position: 'absolute', inset: 0, border: '4px solid #e5e7eb', borderRadius: '50%' }} />
                <div style={{ position: 'absolute', inset: 0, border: '4px solid #7c3aed', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>Importing your data...</h2>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>Please don't close this popup</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* Step 7: Complete */}
          {step === STEPS.COMPLETE && result && (
            <div style={{ padding: '40px 36px', textAlign: 'center' }}>
              <div style={{ width: '64px', height: '64px', background: 'linear-gradient(135deg, #10b981, #059669)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)' }}>
                <svg width="32" height="32" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>Import Complete!</h2>
              <p style={{ margin: '0 0 24px', color: '#6b7280', fontSize: '14px' }}>Your data has been successfully imported</p>

              <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '16px', textAlign: 'left', marginBottom: '24px' }}>
                {result.stats?.departments && (result.stats.departments.created > 0 || result.stats.departments.mapped > 0) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ color: '#374151', fontWeight: '500', fontSize: '14px' }}>Departments</span>
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>{result.stats.departments.created} created, {result.stats.departments.mapped} linked</span>
                  </div>
                )}
                {result.stats?.users && (result.stats.users.created > 0 || result.stats.users.mapped > 0) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ color: '#374151', fontWeight: '500', fontSize: '14px' }}>Users</span>
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>{result.stats.users.created} created, {result.stats.users.mapped} linked</span>
                  </div>
                )}
                {result.stats?.leaveTypes && (result.stats.leaveTypes.created > 0 || result.stats.leaveTypes.mapped > 0) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ color: '#374151', fontWeight: '500', fontSize: '14px' }}>Leave Types</span>
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>{result.stats.leaveTypes.created} created, {result.stats.leaveTypes.mapped} linked</span>
                  </div>
                )}
                {result.stats?.leaveRequests && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                    <span style={{ color: '#374151', fontWeight: '500', fontSize: '14px' }}>Leave Requests</span>
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>{result.stats.leaveRequests.imported} imported, {result.stats.leaveRequests.skipped} skipped</span>
                  </div>
                )}
              </div>

              {/* Debug info */}
              {result.debug && (
                <div style={{ background: '#fef3c7', borderRadius: '8px', padding: '12px', textAlign: 'left', marginBottom: '16px', fontSize: '12px', fontFamily: 'monospace' }}>
                  <div style={{ fontWeight: '600', marginBottom: '6px', color: '#92400e' }}>Debug Info:</div>
                  <div>leaveRequestsEnabled: {String(result.debug.leaveRequestsEnabled)}</div>
                  <div>userMapSize: {result.debug.userMapSize}</div>
                  <div>leaveTypeMapSize: {result.debug.leaveTypeMapSize}</div>
                  <div>userMapKeys: {JSON.stringify(result.debug.userMapKeys)}</div>
                  <div>leaveTypeMapKeys: {JSON.stringify(result.debug.leaveTypeMapKeys)}</div>
                </div>
              )}

              <button onClick={handleClose} style={styles.button(true, false)}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
